import { MsalProvider, useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus, EventType } from "@azure/msal-browser";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import USBLoadingSpinner from "@usb-shield/react-loading-spinner";
import { ROUTE } from "../constants";
import userStore from "../store/userStore";
import { getIdToken } from "./tokenService";
import { loginRequest } from "./msalConfig";
import useTokenRefresh from "../hooks/useTokenRefresh";

// Same loading UI used by Home.js while data loads, reused here so a fresh
// tab/private window resuming the SSO session shows a spinner instead of a
// blank white screen.
const SSOResumingLoader = () => (
  <div className="center-container center-loader center-loader--fullscreen">
    <USBLoadingSpinner datatestid="sso-resuming-spinner">
      Loading...
    </USBLoadingSpinner>
  </div>
);

const SSOAuthGuard = ({ children }) => {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const navigate = useNavigate();
  const { setTokenReady } = userStore();

  // Check MSAL instance directly – React state (useIsAuthenticated) can lag
  // behind the instance cache after handleRedirectPromise runs before mount.
  const hasAccounts = instance.getAllAccounts().length > 0;
  const signedIn = isAuthenticated || hasAccounts;

  // Listen for token events
  useEffect(() => {
    const callbackId = instance.addEventCallback((event) => {
      if (
        (event.eventType === EventType.LOGIN_SUCCESS ||
          event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
        event.payload
      ) {
        const token = event.payload.idToken;
        if (token) {
          sessionStorage.setItem("jwtToken", token);
          setTokenReady(true);
        }
        sessionStorage.setItem("loginType", "sso");
      }

      if (
        event.eventType === EventType.ACQUIRE_TOKEN_FAILURE ||
        event.eventType === EventType.LOGIN_FAILURE
      ) {
        setTokenReady(false);
      }
    });
    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
    };
  }, [instance, setTokenReady]);

  // No cached account: try to resume via the live Entra browser session
  // before giving up to PRE_LOGIN.
  useEffect(() => {
    if (inProgress !== InteractionStatus.None || signedIn) return;
    instance
      .ssoSilent(loginRequest)
      .then((result) => {
        if (result?.account) instance.setActiveAccount(result.account);
      })
      .catch(() => navigate(ROUTE.PRE_LOGIN));
  }, [inProgress, signedIn, instance, navigate]);

  useEffect(() => {
    if (!signedIn) return;
    getIdToken()
      .then((token) => {
        if (token) {
          setTokenReady(true);
        } else {
          // Silent token acquisition failed (e.g. BrowserAuthError "timed_out")
          // and there is no cached token to fall back on. Send the user to the
          // pre-login page instead of leaving them stuck on a blank/loading screen.
          setTokenReady(false);
          navigate(ROUTE.PRE_LOGIN);
        }
      })
      .catch(() => {
        setTokenReady(false);
        navigate(ROUTE.PRE_LOGIN);
      });
  }, [signedIn, setTokenReady, navigate]);

  useTokenRefresh(signedIn);

  if (inProgress !== InteractionStatus.None) {
    return <SSOResumingLoader />;
  }

  if (!signedIn) {
    return <SSOResumingLoader />;
  }

  return children;
};

// MSAL is already initialized and redirect is processed in index.js
// This provider just wraps children with MsalProvider + SSOAuthGuard
const SSOAuthProvider = ({ msalInstance, children }) => {
  return (
    <MsalProvider instance={msalInstance}>
      <SSOAuthGuard>{children}</SSOAuthGuard>
    </MsalProvider>
  );
};

export default SSOAuthProvider;
